import { useMemo } from 'react';
import { useApp } from '../context/AppContext';
import DownloadDataButton from './DownloadDataButton';

/**
 * Overview Diagram Component
 *
 * Shows a visual representation of results with:
 * 1. Top level: Total results by category with W1/2 vs W3/bilateral split
 * 2. Bottom: Planned vs achieved table with traffic lights (for W1/2 only)
 *
 * Funding type split now comes from the EXPLICIT fundingType field
 * set during data consolidation (W1W2 vs W3Bilateral), not derived
 * from the nonPooledProjects field.
 */

// Result type to category mapping
const RESULT_CATEGORIES = {
  'Knowledge product': 'kp',
  'Capacity sharing for development': 'cap',
  'Innovation development': 'innovDev',
  'Other output': 'otherOutput',
  'Innovation use': 'innovUse',
  'Policy change': 'policy',
  'Other outcome': 'otherOutcome',
  'Innovation Package': 'innovPkg',
  'Capacity change': 'capChange',
  'Impact contribution': 'impact',
};

// Category display config
const CATEGORY_CONFIG = {
  cap: {
    label: 'CAP',
    fullName: 'Capacity',
    order: 1,
    isOutput: true,
  },
  kp: {
    label: 'KP',
    fullName: 'Knowledge Products',
    order: 2,
    isOutput: true,
  },
  innovDev: {
    label: 'INNOV DEV',
    fullName: 'Innovation Dev',
    order: 3,
    isOutput: true,
  },
  otherOutput: {
    label: 'OTHER',
    fullName: 'Other Output',
    order: 4,
    isOutput: true,
  },
  innovUse: {
    label: 'INNOV USE',
    fullName: 'Innovation Use',
    order: 5,
    isOutput: false,
  },
  policy: {
    label: 'POLICY',
    fullName: 'Policy Change',
    order: 6,
    isOutput: false,
  },
  otherOutcome: {
    label: 'OTHER',
    fullName: 'Other Outcome',
    order: 7,
    isOutput: false,
  },
  innovPkg: {
    label: 'INNOV PACKAGES',
    fullName: 'Innovation Package',
    order: 8,
    isOutput: false,
  },
};

const OUTPUT_KEYS = Object.entries(CATEGORY_CONFIG)
  .filter(([, c]) => c.isOutput)
  .sort((a, b) => a[1].order - b[1].order)
  .map(([k]) => k);

const OUTCOME_KEYS = Object.entries(CATEGORY_CONFIG)
  .filter(([k, c]) => !c.isOutput && k !== 'innovPkg')
  .sort((a, b) => a[1].order - b[1].order)
  .map(([k]) => k);

function FundingBar({ pooled, nonPooled, total, className = '' }) {
  const pooledPct = total > 0 ? (pooled / total) * 100 : 0;
  const nonPooledPct = total > 0 ? (nonPooled / total) * 100 : 0;
  return (
    <div className={`flex overflow-hidden ${className}`}>
      <div className="bg-emerald-600" style={{ width: `${pooledPct}%` }} />
      <div className="bg-amber-500" style={{ width: `${nonPooledPct}%` }} />
    </div>
  );
}

function CategoryBox({ category, pooled, nonPooled, total, subCategory }) {
  const config = CATEGORY_CONFIG[category];
  if (!config) return null;

  const subConfig = subCategory ? CATEGORY_CONFIG[subCategory.key] : null;
  const hasSub = subCategory && subCategory.total > 0;

  return (
    <div className="flex flex-col items-center flex-1 min-w-0">
      <div
        className="w-full rounded-xl border border-gray-300 overflow-hidden"
        title={`${config.fullName}: ${total.toLocaleString()} total\nW1/W2: ${pooled.toLocaleString()} · W3: ${nonPooled.toLocaleString()}${hasSub ? `\n\n${subConfig.fullName}: ${subCategory.total.toLocaleString()}\nW1/W2: ${subCategory.pooled.toLocaleString()} · W3: ${subCategory.nonPooled.toLocaleString()}` : ''}`}
      >
        {/* Main type */}
        <div className="relative h-16">
          <FundingBar pooled={pooled} nonPooled={nonPooled} total={total} className="absolute inset-0" />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-white font-bold text-xs drop-shadow-md">{config.label}</span>
          </div>
        </div>
        {/* Sub-type (e.g. Innovation Package within Innovation Use) */}
        {hasSub && (
          <div className="relative h-8 border-t border-gray-300/50">
            <FundingBar pooled={subCategory.pooled} nonPooled={subCategory.nonPooled} total={subCategory.total} className="absolute inset-0 opacity-70" />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-white font-bold text-[8px] drop-shadow-md">{subConfig.label}</span>
            </div>
          </div>
        )}
      </div>
      <div className="mt-1.5 text-center">
        <div className="text-xs text-gray-500 leading-tight">{config.fullName}</div>
        <div className="flex items-center justify-center gap-1 text-xs mt-0.5">
          <span className="text-emerald-700 font-medium">{pooled.toLocaleString()}</span>
          <span className="text-gray-400">/</span>
          <span className="text-amber-600 font-medium">{nonPooled.toLocaleString()}</span>
        </div>
        {hasSub && (
          <>
            <div className="text-xs text-gray-400 leading-tight mt-1">{subConfig.fullName}</div>
            <div className="flex items-center justify-center gap-1 text-xs mt-0.5">
              <span className="text-emerald-700 font-medium">{subCategory.pooled.toLocaleString()}</span>
              <span className="text-gray-400">/</span>
              <span className="text-amber-600 font-medium">{subCategory.nonPooled.toLocaleString()}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function OverviewDiagram() {
  const { filteredResults, filters } = useApp();

  // Calculate aggregated data using the EXPLICIT fundingType field
  const aggregatedData = useMemo(() => {
    const categories = {};
    for (const key of Object.keys(CATEGORY_CONFIG)) {
      categories[key] = { pooled: 0, nonPooled: 0, total: 0 };
    }

    let totalPooled = 0;
    let totalNonPooled = 0;

    filteredResults.forEach(result => {
      const resultType = result.type;
      const category = RESULT_CATEGORIES[resultType] || 'other';
      // Use the EXPLICIT fundingType field — this comes from the source file,
      // not derived from nonPooledProjects
      const isPooled = result.fundingType !== 'W3Bilateral';

      if (categories[category]) {
        categories[category].total++;
        if (isPooled) {
          categories[category].pooled++;
          totalPooled++;
        } else {
          categories[category].nonPooled++;
          totalNonPooled++;
        }
      }
    });

    const sum = (keys) => keys.reduce((acc, k) => {
      acc.pooled += categories[k].pooled;
      acc.nonPooled += categories[k].nonPooled;
      acc.total += categories[k].total;
      return acc;
    }, { pooled: 0, nonPooled: 0, total: 0 });

    const outputTotals = sum(OUTPUT_KEYS);
    const outcomeTotals = sum([...OUTCOME_KEYS, 'innovPkg']);

    return { categories, totalPooled, totalNonPooled, total: filteredResults.length, outputTotals, outcomeTotals };
  }, [filteredResults]);

  // Data subsets for download
  const w1w2Results = useMemo(
    () => filteredResults.filter(r => r.fundingType !== 'W3Bilateral'),
    [filteredResults]
  );
  const w3Results = useMemo(
    () => filteredResults.filter(r => r.fundingType === 'W3Bilateral'),
    [filteredResults]
  );

  const selectedYear = filters?.year || 'All Years';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="text-center flex-1">
          <h3 className="text-lg font-semibold text-gray-900">
            Results Overview {selectedYear !== 'All Years' && `(${selectedYear})`}
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Total results reported with W1/2 & W3/bilateral split
          </p>
        </div>
        <DownloadDataButton
          data={filteredResults}
          filename="overview-all-results"
          label="Download all"
          subsetLabel="All Results"
          compact
        />
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-emerald-600" />
          <span className="text-gray-600">W1/W2 (Pooled)</span>
          <span className="font-medium text-emerald-700">{aggregatedData.totalPooled.toLocaleString()}</span>
          <DownloadDataButton data={w1w2Results} filename="overview-w1w2-results" label="W1/W2" subsetLabel="W1W2 Pooled" compact />
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-amber-500" />
          <span className="text-gray-600">W3/Bilateral</span>
          <span className="font-medium text-amber-600">{aggregatedData.totalNonPooled.toLocaleString()}</span>
          <DownloadDataButton data={w3Results} filename="overview-w3-results" label="W3" subsetLabel="W3 Bilateral" compact />
        </div>
      </div>

      {/* Category Boxes */}
      <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-0">
        <div className="flex-1 min-w-0">
          <div className="border-2 border-dashed border-gray-300 rounded-xl px-3 py-3 bg-gray-100 h-full">
            <div className="text-center mb-2">
              <div className="text-xs text-gray-600 font-semibold uppercase tracking-wide">Outputs</div>
              <div className="flex items-center justify-center gap-1 text-xs mt-0.5">
                <span className="text-emerald-700 font-medium">{aggregatedData.outputTotals.pooled.toLocaleString()}</span>
                <span className="text-gray-400">/</span>
                <span className="text-amber-600 font-medium">{aggregatedData.outputTotals.nonPooled.toLocaleString()}</span>
              </div>
            </div>
            <div className="flex flex-wrap items-start gap-2 sm:gap-3">
              {OUTPUT_KEYS
                .filter(k => aggregatedData.categories[k].total > 0)
                .map(k => (
                  <CategoryBox
                    key={k}
                    category={k}
                    pooled={aggregatedData.categories[k].pooled}
                    nonPooled={aggregatedData.categories[k].nonPooled}
                    total={aggregatedData.categories[k].total}
                  />
                ))}
            </div>
          </div>
        </div>

        <div className="hidden sm:flex items-center px-1 shrink-0">
          <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <line x1="3" y1="12" x2="17" y2="12" strokeWidth={3} strokeLinecap="square" />
            <path strokeLinecap="square" strokeLinejoin="miter" strokeWidth={3} d="M15 7l6 5-6 5" />
          </svg>
        </div>

        <div className="flex-1 min-w-0">
          <div className="border-2 border-dashed border-gray-300 rounded-xl px-3 py-3 bg-gray-100 h-full">
            <div className="text-center mb-2">
              <div className="text-xs text-gray-600 font-semibold uppercase tracking-wide">Outcomes</div>
              <div className="flex items-center justify-center gap-1 text-xs mt-0.5">
                <span className="text-emerald-700 font-medium">{aggregatedData.outcomeTotals.pooled.toLocaleString()}</span>
                <span className="text-gray-400">/</span>
                <span className="text-amber-600 font-medium">{aggregatedData.outcomeTotals.nonPooled.toLocaleString()}</span>
              </div>
            </div>
            <div className="flex flex-wrap items-start gap-2 sm:gap-3">
              {OUTCOME_KEYS
                .filter(k => {
                  let t = aggregatedData.categories[k].total;
                  if (k === 'innovUse') t += aggregatedData.categories.innovPkg.total;
                  return t > 0;
                })
                .map(k => (
                  <CategoryBox
                    key={k}
                    category={k}
                    pooled={aggregatedData.categories[k].pooled}
                    nonPooled={aggregatedData.categories[k].nonPooled}
                    total={aggregatedData.categories[k].total}
                    {...(k === 'innovUse' ? { subCategory: { key: 'innovPkg', ...aggregatedData.categories.innovPkg } } : {})}
                  />
                ))}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
